import type { ChildConversation, RActiveConversation } from './api/conversations'

export interface PlivoCallInfo {
  callUUID: string
  callerNumber: string
  contactName?: string
  extraHeaders?: Record<string, string>
}

export enum CallDirection {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

export type CallMeta =
  | 'Outgoing call'
  | 'Incoming call'
  | 'Incoming call transfer'
  | 'Outgoing call transfer'
  | 'Monitored Call'

export interface CallDetails {
  callUUID: string
  // https://docs.plivo.com/docs/voice/troubleshooting/hangup-causes#list-of-hangup-causes
  code: number
  reason?: string
  direction: CallDirection
  startTime: Date
  callWith: string
  extraHeaders?: {
    meta?: CallMeta
    aliasName?: string
    teamName?: string
    fromAgent?: string
  }
  aLegUUID?: string
  aLegToNumber?: string
  contactName?: string
  autoAcceptDelay?: number
  autoAccept?: boolean
  meta?: CallMeta
  isOnnetCall?: boolean
  isAnonymous?: boolean
}

/**
 * Call States:
 * ------------
 * 1. Active Call (activeCall !== null):
 *    - Indicates a call attempt is in progress
 *    - Set when call is initiated (outbound) or received (inbound)
 *    - Does NOT mean the call is connected yet
 *    - Used for tracking call metadata and state
 *
 * 2. Call Connected (isCallActive === true):
 *    - Indicates the call is actually connected
 *    - Set when onCallAnswered event fires
 *    - For outbound: recipient has answered
 *    - For inbound: local user has answered
 *    - Audio communication is possible in this state
 */

export interface CallConversation {
  callChildConversation?: ChildConversation
  parentConversation?: RActiveConversation
}

export interface PhoneState {
  activeCall: CallDetails | null
  isCallActive: boolean
  isMuted: boolean
  isOnHold: boolean
  isRegistered: boolean // whether the primary tab is registered with the phone SDK
  callConversation: CallConversation
  participants: Participant[]
  callPickedup: boolean
  isRecordingPaused: boolean
  isSecondaryParticipant: boolean
  monitoringState: MonitorStates | null
  isMonitorInitiator: boolean // To check if the current user is the one who started the monitoring
}

export interface Participant {
  identifier: string // name of the participant
  member: string // agentID or phone number of the participant
  startAt: string | null
  callUUID?: string
  isOnHold?: boolean
  state: ParticipantState
  isExternal?: boolean
  participantType: 'agent' | 'team' | 'external' | 'monitor' | null
}
export interface PhoneStore extends PhoneState {
  setActiveCall: (call: Partial<CallDetails> | null) => void
  setCallActive: (status: boolean) => void
  setMuted: (status: boolean) => void
  setOnHold: (status: boolean) => void
  reset: (clearConnectionState?: boolean) => void
  setRegistered: (status: boolean) => void
  setCallConversation: (conversation?: RActiveConversation[]) => void
  setCallPickedup: (status: boolean) => void
  setIsRecordingPaused: (status: boolean) => void
  // Participant Actions
  updateParticipant: (member: string, participant: Partial<Participant>) => void
  addParticipant: (participant: Participant) => void
  removeParticipant: (member: string) => void
  clearParticipants: () => void
  // When a warm transfer call is picked up, the agent is the secondary participant
  setIsSecondaryParticipant: (status: boolean) => void
  setIsMonitorInitiator: (status: boolean) => void
  setMonitoringState: (state: MonitorStates | null) => void
}
export interface PlivoConfig {
  username: string
  password: string
  onLoggedIn?: () => void
  onLoggedOut?: () => void
  onCallReceived?: (call: CallDetails) => void
  onCallEnded?: () => void
}

export enum ParticipantState {
  CONNECTING_COLD_TRANSFER = 'CONNECTING_COLD_TRANSFER',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  MERGED = 'MERGED',
  FAILED = 'FAILED',
}

export enum ParticipantAction {
  call = 'call',
  merge = 'merge',
  hold = 'hold',
  switch = 'switch',
}

export interface RecordingPayload {
  call_direction: CallDirection
  agent_sip_endpoint: string
  pauseRecording: boolean
}

export enum MonitorStates {
  BARGE = 'barge', // from BE in conversation model
  COACH = 'coach', // from BE in conversation model
  MONITOR = 'monitor', // from BE in conversation model
  MONITOR_VISIBLE = 'monitor_visible', // from BE in conversation model
  PAUSE = 'monitor_paused', // from BE in conversation model
  REJECT = 'reject',
  NOTIFY = 'notify',
  MUTE = 'mute',
  REQUEST_FOR_HELP = 'request_help',
  ASSISTANCE_REJECT = 'assistance_reject',
  ASSISTANCE_REQUESTED = 'assistance_requested',
  SWITCH_CALL = 'switch',
  RESUME = 'resume',
  START = 'start',
  LEAVE = 'leave',
}
